/**
 * Deep links into CloudWatch Logs, at the right log group and the right minute. F-83.
 *
 * The point is the *time range*. "Why did that question go out at 03:10" is answered
 * by twenty minutes of the sweep's log around 03:10, and the difference between a
 * link that carries those twenty minutes and a link that opens the log group is ten
 * minutes of scrolling per question.
 *
 * These open in the AWS console, so they only resolve for a browser already signed
 * into account 598322318426 (CLAUDE.md, "Account facts"). A signed-out click lands on
 * the AWS sign-in page rather than failing oddly, which is an acceptable outcome for
 * a link the one operator uses.
 */

/** Everything nabu logs to, from `infra/lib/*-stack.ts`. Prefixed `/nabu/`, per rule 6. */
export const LOG_GROUPS = {
  research: '/nabu/research',
  synthesise: '/nabu/synthesise',
  itemsSubmit: '/nabu/items-submit',
  itemsPoll: '/nabu/items-poll',
  itemsRetrieve: '/nabu/items-retrieve',
  sweep: '/nabu/sweep',
  inbound: '/nabu/inbound',
  api: '/nabu/api',
} as const;

const REGION = 'us-east-1';

/**
 * The console's hash-fragment encoding, which is not URI encoding.
 *
 * CloudWatch escapes a percent as `$`, so `%3F` becomes `$3F` — and a log group name
 * has to be encoded *twice* before that, because its slashes must survive being read
 * as one path segment: `/nabu/sweep` arrives as `$252Fnabu$252Fsweep`.
 *
 * Both halves are worth having as functions rather than as a template full of `$25`
 * literals. A link with the group encoded once looks right, opens, and shows "log
 * group does not exist" — a failure that reads as missing infrastructure rather than
 * as a bad link, which is the wrong thing to make an operator debug.
 */
const dollarEncode = (value: string) => encodeURIComponent(value).replace(/%/g, '$');

/** `?`, `=` and `&` inside the fragment, which the console wants as `$3F$3D$26`. */
const QUERY = dollarEncode('?');
const EQUALS = dollarEncode('=');
const AND = dollarEncode('&');

/**
 * A log group, searched across all streams, over one window.
 *
 * The window is passed in rather than defaulted, because every caller knows something
 * better than "the last hour": a send knows when it was sent, a pass knows it finished
 * within the day.
 */
export function logsUrl(group: string, fromMs: number, toMs: number): string {
  const target = dollarEncode(encodeURIComponent(group));
  const range = `${QUERY}start${EQUALS}${Math.round(fromMs)}${AND}end${EQUALS}${Math.round(toMs)}`;
  return `https://${REGION}.console.aws.amazon.com/cloudwatch/home?region=${REGION}#logsV2:log-groups/log-group/${target}/log-events${range}`;
}

/** Ten minutes either side of an instant — enough of the sweep's log to see the decision. */
export function logsAround(group: string, iso: string, minutes = 10): string | undefined {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return undefined;
  return logsUrl(group, at - minutes * 60_000, at + minutes * 60_000);
}

/** The last `hours` up to now, for a pass whose finishing time nothing recorded. */
export function logsRecent(group: string, hours = 24): string {
  const now = Date.now();
  return logsUrl(group, now - hours * 3_600_000, now);
}
